import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { listTemplates, previewTemplate, resetTemplate, updateTemplate, type TEMPLATE_LANGUAGES } from '@mkcore/collab/schema';
import { Badge, Button, Card, ErrorState, Field, Input, PageHeader, Skeleton, Textarea } from '@mkcore/ui';
import { ApiError, call } from '@/lib/api';
import { useActionMutation, useErrorMessage } from '@/lib/mutation';
import { useCan } from '@/lib/session';

export const Route = createFileRoute('/_app/admin/templates')({ component: Templates });

const templatesQuery = queryOptions({ queryKey: ['admin', 'template'], queryFn: () => call(listTemplates, {}) });
type Lang = (typeof TEMPLATE_LANGUAGES)[number];

/** Mail and notification templates per language (CO-0558): the text with {{placeholders}}, a preview and a reset. */
function Templates() {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const q = useQuery(templatesQuery);
  const [open, setOpen] = useState<string | null>(null);
  if (q.isError) return <ErrorState title={t('errors.generic')} description={message(q.error)} />;
  if (!q.data) return <Skeleton className="h-96" />;
  return (
    <>
      <PageHeader title={t('templates.title')} description={t('templates.description')} />
      <div className="grid gap-3">
        {q.data.items.map((tpl) => (
          <Card key={tpl.key} className="grid gap-2 p-4">
            <button type="button" className="flex flex-wrap items-center gap-2 text-left" onClick={() => setOpen(open === tpl.key ? null : tpl.key)} aria-expanded={open === tpl.key}>
              <span className="font-bold">{tpl.title}</span>
              {tpl.variants.some((v) => v.customized) && <Badge tone="info">{t('templates.customized')}</Badge>}
              <span className="text-xs text-fg-3">{tpl.key}</span>
            </button>
            <p className="text-sm text-fg-2">{tpl.description}</p>
            {open === tpl.key && (
              <div className="grid gap-4 lg:grid-cols-2">
                {tpl.variants.map((v) => (
                  <Variant key={v.language} templateKey={tpl.key} language={v.language} subject={v.subject} body={v.body} customized={v.customized} placeholders={tpl.placeholders} />
                ))}
              </div>
            )}
          </Card>
        ))}
      </div>
    </>
  );
}

function Variant(props: { templateKey: string; language: Lang; subject: string; body: string; customized: boolean; placeholders: { name: string; description: string }[] }) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const canUpdate = useCan('admin:update');
  const [subject, setSubject] = useState(props.subject);
  const [body, setBody] = useState(props.body);
  const [preview, setPreview] = useState<{ subject: string; body: string } | null>(null);
  const [error, setError] = useState('');
  const invalidate = [['admin', 'template']];
  const save = useActionMutation(updateTemplate, { invalidate, success: t('common.saved'), silentErrors: true });
  const reset = useActionMutation(resetTemplate, { invalidate, success: t('templates.resetDone'), onSuccess: (tpl) => {
    const v = tpl.variants.find((x) => x.language === props.language)!;
    setSubject(v.subject);
    setBody(v.body);
  } });
  const show = useActionMutation(previewTemplate, { invalidate: [], onSuccess: setPreview });
  const input = { key: props.templateKey, language: props.language };
  return (
    <div className="grid content-start gap-2">
      <h3 className="text-sm font-bold uppercase text-fg-3">{props.language === 'nl' ? 'Nederlands' : 'English'}</h3>
      <Field label={t('templates.subject')}>{(p) => <Input {...p} value={subject} disabled={!canUpdate} onChange={(e) => setSubject(e.target.value)} />}</Field>
      <Field label={t('templates.body')}>{(p) => <Textarea {...p} rows={10} value={body} disabled={!canUpdate} onChange={(e) => setBody(e.target.value)} />}</Field>
      <p className="text-xs text-fg-3">
        {t('templates.placeholders')}: {props.placeholders.map((p) => <code key={p.name} title={p.description} className="mr-1">{`{{${p.name}}}`}</code>)}
      </p>
      {error && (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        {canUpdate && (
          <Button
            loading={save.isPending}
            onClick={() => save.mutate({ input: { ...input, subject, body } }, { onError: (e) => setError(e instanceof ApiError ? message(e) : String(e)), onSuccess: () => setError('') })}
          >
            {t('common.save')}
          </Button>
        )}
        <Button variant="secondary" loading={show.isPending} onClick={() => show.mutate({ input: { ...input, subject, body } })}>
          {t('templates.preview')}
        </Button>
        {canUpdate && props.customized && (
          <Button variant="ghost" loading={reset.isPending} onClick={() => reset.mutate({ input })}>
            {t('templates.reset')}
          </Button>
        )}
      </div>
      {preview && (
        <div className="rounded-sm border border-line bg-sunken p-3 text-sm" data-testid="template-preview">
          <div className="font-bold">{preview.subject}</div>
          <pre className="mt-2 whitespace-pre-wrap font-sans">{preview.body}</pre>
        </div>
      )}
    </div>
  );
}
